import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MessageCircle, Clock, Shield } from "lucide-react";
import { Card } from "@/components/ui/card";

const About = () => {
  const values = [
    {
      icon: MessageCircle,
      title: "Human-first AI",
      description: "Friendly, natural, and helpful."
    },
    {
      icon: Clock,
      title: "Always available",
      description: "Never miss a lead or opportunity."
    },
    {
      icon: Shield,
      title: "Trust & privacy",
      description: "100% GDPR-compliant, hosted in Belgian data centers."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                We're on a Mission to Help Belgian Businesses{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Never Miss a Call Again
                </span>
              </h1>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                Founded in Brussels, SmartCall AI was built for the unique multilingual needs of Belgian businesses.
              </p>
              <p className="text-xl text-foreground leading-relaxed">
                Our AI receptionist understands your customers — whether they speak Dutch, French, English, or German — and provides instant, professional service 24/7.
              </p>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Vision</h2>
                <p className="text-xl text-muted-foreground">
                  To make AI-driven communication effortless and accessible for every local business.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Our Values</h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {values.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <Card key={index} className="p-8 text-center border-border">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent mb-4">
                        <Icon className="w-8 h-8 text-primary-foreground" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                      <p className="text-muted-foreground">{value.description}</p>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Team</h2>
              <p className="text-xl text-muted-foreground mb-8">
                We're a passionate group of engineers, linguists, and business experts on a mission to redefine customer communication.
              </p>
              
              <div className="flex flex-col items-center gap-4 mb-8">
                <div className="flex items-center gap-2 text-lg">
                  <span className="text-2xl">📍</span>
                  <span className="font-medium">Based in Brussels, Belgium</span>
                </div>
                <div className="flex items-center gap-2 text-lg">
                  <span className="text-2xl">📧</span>
                  <a 
                    href="mailto:hello@smartcall.be" 
                    className="text-primary hover:underline font-medium"
                  >
                    hello@smartcall.be
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="hero">
                  Meet the Team
                </Button>
                <Button size="lg" variant="outline">
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
